import { ConfigProvider, Empty, message, Pagination, Table, Tag, theme, Tooltip, Button, Space } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { MakeApiCall } from '../../../../../apis';
import { AddCommas, ConvertParams, defaultPageSize } from '../../../../../config';
import { fakeProImage } from '../../../../../config/fakeData';
import { getAmount } from "../../../../../utils/helper";
import { AmazonFeesBreakdownTooltip, amazonFeesTooltipBodyStyle } from "../../../../../utils/amazonFeesBreakdown";
import OrderDetailsModal from "../../../../../components/modal/OrderDetailsModal";
import { useVisibleColumns } from '../../../../../hooks/useVisibleColumns';
import { COLUMN_PREF_PAGES } from '../../../../../utils/columnPreferences';

// ─── NEW: Constants ───────────────────────────────────────────────────────────
const SELLABLE   = 1;
const UNSELLABLE = 0;

// ─── NEW: Pure helpers (defined outside component) ────────────────────────────

/** Priority: local statusMap → API field → undefined */
const resolveStatus = (record, statusMap) => {
    if (statusMap[record.order_id] !== undefined) return statusMap[record.order_id];
    if (record.is_sellable_status  !== undefined) return record.is_sellable_status;
    return undefined;
};

/** A row is eligible only when refund_unit > 0 */
const isRowEligible = (record) => Number(record.refund_unit) > 0;

const toApiOperator = op =>
    ({ gt: '>', lt: '<', gte: '>=', lte: '<=', eq: '=', contains: 'contains' }[op] || op);

export const buildOrderItemFilterParams = (currentFilters) =>
    currentFilters.length > 0
        ? currentFilters.map(f => ({ attribute: f.column, operator: toApiOperator(f.operator), value: f.value }))
        : undefined;

/** UI column `key` used for Table sort → `sort_by` field name expected by the ordered-products API. */
const ORDER_ITEMS_SORT_KEY_TO_API = {
    cost_of_goods: 'total_cogs',
};

function compareParamsFromTile(tile) {
    if (tile?.is_compare === '1' && tile?.compare_with_start_date && tile?.compare_with_end_date) {
        return {
            is_compare: '1',
            compare_with_start_date: tile.compare_with_start_date,
            compare_with_end_date: tile.compare_with_end_date,
        };
    }
    return { is_compare: '0' };
}

const getNestedValue = (row, path) => {
    if (!row) return undefined;
    if (Array.isArray(path)) {
        return path.reduce((acc, key) => (acc == null ? undefined : acc[key]), row);
    }
    return row[path];
};

const calculateChangePercent = (currentValue, previousValue) => {
    const current = Number(currentValue);
    const previous = Number(previousValue);
    if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
    if (current === 0) {
        if (previous === 0) return 0;
        return null;
    }
    return ((current - previous) / Math.abs(current)) * 100;
};

const formatComparePct = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
};

/**
 * Use array rows as-is. Avoid Object.values(records) on arrays — enumerable extras
 * (or mixed shapes) can inflate row count vs pagination.totalCount.
 *
 * Both branches require `order_id != null` so any embedded meta/summary entries
 * the backend may emit (with or without numeric keys) are filtered out before
 * they can render as a phantom row.
 */
export const normalizeOrderedProductRecords = (records) => {
    if (records == null) return [];
    const list = Array.isArray(records) ? records : Object.values(records);
    return list.filter(r => r && typeof r === 'object' && r.order_id != null);
};

const attachCompareRowsToOrders = (records, compareWith) => {
    const compareRecords = normalizeOrderedProductRecords(compareWith?.records);
    if (!compareRecords.length) return records;

    const compareByOrderId = new Map(
        compareRecords
            .filter(row => row?.order_id != null)
            .map(row => [String(row.order_id), row])
    );

    return records.map((record, index) => {
        const compareRow = compareByOrderId.get(String(record.order_id)) || compareRecords[index];
        if (!compareRow) return record;

        return {
            ...record,
            compare_with: {
                start_date: compareWith?.start_date,
                end_date: compareWith?.end_date,
                row: compareRow,
            },
        };
    });
};

/** Server-side filter_params columns (dropdown options from get-saved-presets / API where applicable). */
export const ORDER_ITEMS_FILTERABLE_COLUMNS = [
    { key: 'order_id', label: 'Order ID', type: 'text' },
    { key: 'product_name', label: 'Product name', type: 'text' },
    { key: 'sku', label: 'SKU', type: 'text' },
    { key: 'asin', label: 'ASIN', type: 'text' },
    { key: 'sale_channel', label: 'Sale channel', type: 'text' },
    { key: 'order_status', label: 'Order Status', type: 'dropdown' },
    { key: 'sellable_status', label: 'Sellable Status', type: 'dropdown' },
    { key: 'price', label: 'Price', type: 'numeric' },
    { key: 'unit_sold', label: 'Units Sold', type: 'numeric' },
    { key: 'refund', label: 'Refund amount', type: 'numeric' },
    { key: 'refund_unit', label: 'Refund units', type: 'numeric' },
    { key: 'refund_status', label: 'Refund status', type: 'numeric' },
    { key: 'sales', label: 'Sales', type: 'numeric' },
    { key: 'amazon_fees', label: 'Amazon fees', type: 'numeric' },
    { key: 'gross_profit', label: 'Gross profit', type: 'numeric' },
    { key: 'net_profit', label: 'Net profit', type: 'numeric' },
    { key: 'margin', label: 'Margin', type: 'numeric' },
    { key: 'conversion_rate', label: 'Conversion rate', type: 'numeric' },
    { key: 'product_cogs', label: 'Product COGS', type: 'numeric' },
    { key: 'product_vat_rate', label: 'Product VAT rate', type: 'numeric' },
    { key: 'total_cogs', label: 'Total COGS', type: 'numeric' },
    { key: 'total_vat', label: 'Total VAT', type: 'numeric' },
    { key: 'tax_collected_on_sales', label: 'Tax collected on sales', type: 'numeric' },
    { key: 'item_tax', label: 'Item tax', type: 'numeric' },
    { key: 'shipping_tax', label: 'Shipping tax', type: 'numeric' },
    { key: 'gift_wrap_tax', label: 'Gift wrap tax', type: 'numeric' },
    { key: 'is_easy_ship_order', label: 'Easy Ship order', type: 'numeric' },
    { key: 'is_business_order', label: 'Business order', type: 'numeric' },
    { key: 'promo', label: 'Promo', type: 'numeric' },
    { key: 'gift_wrap', label: 'Gift wrap', type: 'numeric' },
    { key: 'shipping_cost', label: 'Shipping cost', type: 'numeric' },
    { key: 'cost_of_goods', label: 'Cost of goods', type: 'numeric' },
    { key: 'vat', label: 'VAT (detail)', type: 'numeric' },
    { key: 'estimated_payout', label: 'Estimated payout', type: 'numeric' },
];

// ─── NEW: SellableTag sub-component ──────────────────────────────────────────
const SellableTag = ({ status }) => {
    if (status === SELLABLE)   return <span style={{padding: '4px 9px', fontWeight: 700}} className="badge badge-primary"> Sellable </span>;
    if (status === UNSELLABLE) return <span style={{padding: '4px 9px', fontWeight: 700}} className="badge badge-danger"> Unsellable </span>;
    return                            <span style={{padding: '4px 9px', fontWeight: 700}} className="badge badge-secondary"> Not Set </span>;
};

// ─── NEW: BulkActionsBar sub-component ───────────────────────────────────────
const BulkActionsBar = ({ selectedRows, onMarkSellable, onMarkUnsellable, actionLoading }) => {
    if (!selectedRows.length) return null;

    const validCount   = selectedRows.filter(isRowEligible).length;
    const invalidCount = selectedRows.length - validCount;
    const hasValid     = validCount > 0;

    return (
        <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 p-3 rounded-3"
            style={{ background: '#f0f5ff', border: '1px solid #adc6ff' }}
        >
            <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="fw-bold text-primary fs-7">
                    {selectedRows.length} row{selectedRows.length > 1 ? 's' : ''} selected
                </span>
                {invalidCount > 0 && (
                    <Tag color="warning" style={{ margin: 0 }}>
                        {invalidCount} ineligible (refund = 0)
                    </Tag>
                )}
            </div>
            <Space wrap>
                <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    loading={actionLoading === 'sellable'}
                    disabled={!hasValid || !!actionLoading}
                    onClick={() => onMarkSellable(selectedRows)}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                >
                    Mark as Sellable
                </Button>
                <Button
                    type="primary"
                    danger
                    size="small"
                    icon={<CloseCircleOutlined />}
                    loading={actionLoading === 'unsellable'}
                    disabled={!hasValid || !!actionLoading}
                    onClick={() => onMarkUnsellable(selectedRows)}
                >
                    Mark as Unsellable
                </Button>
            </Space>
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const OrderItems = ({ userData, selectedMarketplace, selectedCurrencySign, selectedTileData, isEasyShipOrder, onColumnsReady, onFiltersReady }) => {
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);
    const [totalPage, setTotalPage] = useState(0);
    const [summaryData, setSummaryData] = useState(null);
    const lastSummaryKeyRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const tableContainerRef = useRef(null);
    const tokenRef = useRef(userData?.token);
    const listFetchGenRef = useRef(0);
    const summaryFetchGenRef = useRef(0);
    const isFirstRender = useRef(true);

    useEffect(() => { tokenRef.current = userData?.token; }, [userData?.token]);

    // Modal State
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // ─── NEW: Bulk-action state ───────────────────────────────────────────────
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows]       = useState([]);
    const [actionLoading, setActionLoading]     = useState(null); // 'sellable' | 'unsellable' | null
    /** Local override map { [order_id]: 0 | 1 } — for optimistic UI updates */
    const [statusMap, setStatusMap] = useState({});

    const [activeFilters, setActiveFilters]       = useState([]);
    const [sortState, setSortState]               = useState({ columnKey: null, order: null });
    const [filterDropdownOptions, setFilterDropdownOptions] = useState({});

    const [filter, setFilter] = useState({
        page: 1,
        "per-page": defaultPageSize,
        marketplace_id: selectedMarketplace?.id,
        start_date: selectedTileData?.start_date,
        end_date: selectedTileData?.end_date,
        sku: selectedTileData?.sku,
        ...compareParamsFromTile(selectedTileData),
        is_easy_ship_order: isEasyShipOrder,
    });

    const addFilter = useCallback(filterObj => {
        setActiveFilters(prev => [...prev, filterObj]);
        setFilter(prev => ({ ...prev, page: 1 }));
    }, []);

    const removeFilter = useCallback(id => {
        setActiveFilters(prev => prev.filter(f => f.id !== id));
        setFilter(prev => ({ ...prev, page: 1 }));
    }, []);

    const updateFilter = useCallback((id, updatedFields) => {
        setActiveFilters(prev => prev.map(f => (f.id === id ? { ...f, ...updatedFields } : f)));
        setFilter(prev => ({ ...prev, page: 1 }));
    }, []);

    const clearFilters = useCallback(() => {
        setActiveFilters([]);
        setFilter(prev => ({ ...prev, page: 1 }));
    }, []);

    const handleTableChange = useCallback((_, __, sorter) => {
        setSortState({
            columnKey: sorter.order ? sorter.columnKey : null,
            order:     sorter.order || null,
        });
        setFilter(prev => ({ ...prev, page: 1 }));
    }, []);

    /** Shared query fields for list + totals; `_t` bypasses HTTP caches on every interaction. */
    const mergeOrderedParams = (filterBase, currentFilters, currentSort, { includePagination } = { includePagination: true }) => {
        const filterParamsArr = buildOrderItemFilterParams(currentFilters);
        const sortColumnKey = currentSort.columnKey || null;
        const sortBy = sortColumnKey ? (ORDER_ITEMS_SORT_KEY_TO_API[sortColumnKey] || sortColumnKey) : null;
        const sortOrder = sortColumnKey
            ? (currentSort.order === 'ascend' ? 'asc' : 'desc')
            : null;
        return {
            marketplace_id: filterBase.marketplace_id,
            start_date:     filterBase.start_date,
            end_date:       filterBase.end_date,
            ...(filterBase.is_compare !== undefined && filterBase.is_compare !== null
                ? { is_compare: filterBase.is_compare }
                : {}),
            ...(filterBase.compare_with_start_date ? { compare_with_start_date: filterBase.compare_with_start_date } : {}),
            ...(filterBase.compare_with_end_date ? { compare_with_end_date: filterBase.compare_with_end_date } : {}),
            ...(filterBase.sku ? { sku: filterBase.sku } : {}),
            ...(filterBase.is_easy_ship_order !== undefined && filterBase.is_easy_ship_order !== null
                ? { is_easy_ship_order: filterBase.is_easy_ship_order }
                : {}),
            ...(includePagination
                ? { page: filterBase.page, 'per-page': filterBase['per-page'] }
                : {}),
            _t: Date.now(),
            ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
            ...(filterParamsArr ? { filter_params: JSON.stringify(filterParamsArr) } : {}),
        };
    };

    // --- Helpers ---
    const handleResize = useCallback(() => setIsMobile(window.innerWidth < 768), []);

    // bannerHeight is used for the table sticky header offset
    const bannerHeight = userData?.user?.user_data?.trial_expires_at ? 40 : 0;

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    const fetchSummaryData = useCallback(async (filterBase, currentFilters, currentSort) => {
        if (!filterBase?.marketplace_id) return;
        const token = tokenRef.current;
        const params = mergeOrderedParams(filterBase, currentFilters, currentSort, { includePagination: false });
        const fetchGen = ++summaryFetchGenRef.current;
        try {
            const response = await MakeApiCall(
                `dashboard-optimized/tiles/get-ordered-product-analytics-data-total${ConvertParams(params)}`,
                'get',
                null,
                true,
                {},
                token
            );
            if (fetchGen !== summaryFetchGenRef.current) return;
            if (response?.status) {
                setSummaryData(response.data);
            }
        } catch (error) {
            if (fetchGen === summaryFetchGenRef.current) {
                console.error('Error fetching order summary data:', error);
            }
        }
    }, []);

    const maybeFetchSummaryData = useCallback((filterBase, currentFilters, currentSort) => {
        if (!filterBase?.marketplace_id) return;
        const nextKey = JSON.stringify({
            marketplace_id: filterBase.marketplace_id || null,
            start_date: filterBase.start_date || null,
            end_date: filterBase.end_date || null,
            sku: filterBase.sku || '',
            is_compare: filterBase.is_compare || '0',
            compare_with_start_date: filterBase.compare_with_start_date || null,
            compare_with_end_date: filterBase.compare_with_end_date || null,
            is_easy_ship_order: filterBase.is_easy_ship_order ?? null,
            filters: currentFilters,
            sort: currentSort,
        });
        if (lastSummaryKeyRef.current === nextKey) return;
        lastSummaryKeyRef.current = nextKey;
        fetchSummaryData(filterBase, currentFilters, currentSort);
    }, [fetchSummaryData]);

    const fetchOrderList = useCallback(async (filterBase, currentFilters, currentSort) => {
        if (!filterBase?.marketplace_id) {
            setList([]);
            setTotalPage(0);
            return;
        }

        const token = tokenRef.current;
        const fetchGen = ++listFetchGenRef.current;

        setLoading(true);
        try {
            const params = mergeOrderedParams(filterBase, currentFilters, currentSort, { includePagination: true });
            const response = await MakeApiCall(
                `dashboard-optimized/tiles/get-ordered-product-analytics-data${ConvertParams(params)}`,
                'get',
                null,
                true,
                {},
                token
            );
            if (fetchGen !== listFetchGenRef.current) {
                // DEBUG: surfaces dropped stale responses if a phantom row is reported again
                console.log('[OrderItems] Discarded stale list response', {
                    fetchGen,
                    currentGen: listFetchGenRef.current,
                    requestParams: params,
                });
                return;
            }
            if (response?.status) {
                const pagination = response?.data?.pagination || {};
                setTotalPage(pagination.totalCount || 0);
                const recordsArray = attachCompareRowsToOrders(
                    normalizeOrderedProductRecords(response?.data?.records),
                    response?.data?.compare_with
                );
                // DEBUG: remove after verifying table payload vs API
                console.log('[OrderItems] Table dataSource (before setList / display)', {
                    fetchGen,
                    dataSource: recordsArray,
                    rowCount: recordsArray.length,
                    pagination,
                    rawRecordsType: Array.isArray(response?.data?.records) ? 'array' : typeof response?.data?.records,
                    rawRecords: response?.data?.records,
                    requestParams: params,
                });
                setList(recordsArray);
                setFilter(prev => {
                    const nextPage = pagination.page || prev.page;
                    const nextPer  = pagination.page_size || prev['per-page'];
                    if (nextPage === prev.page && nextPer === prev['per-page']) return prev;
                    return { ...prev, page: nextPage, 'per-page': nextPer };
                });
                if (response?.data?.filter_dropdown_options) {
                    setFilterDropdownOptions(response.data.filter_dropdown_options);
                }
                setSelectedRowKeys([]);
                setSelectedRows([]);
                maybeFetchSummaryData(filterBase, currentFilters, currentSort);
            } else {
                message.warning(response?.message);
                setList([]);
            }
        } catch (error) {
            if (fetchGen === listFetchGenRef.current) {
                message.error('Something went wrong while fetching data');
                setList([]);
            }
        } finally {
            if (fetchGen === listFetchGenRef.current) {
                setLoading(false);
            }
        }
    }, [maybeFetchSummaryData]);

    // Add a useEffect to watch for isEasyShipOrder prop changes
    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            is_easy_ship_order: isEasyShipOrder,
            page: 1,
        }));
    }, [isEasyShipOrder]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setActiveFilters([]);
        setSortState({ columnKey: null, order: null });
        lastSummaryKeyRef.current = null;
        listFetchGenRef.current += 1;
        setFilter(prev => ({
            ...prev,
            page: 1,
            marketplace_id: selectedMarketplace?.id,
            start_date:     selectedTileData?.start_date,
            end_date:       selectedTileData?.end_date,
            sku:            selectedTileData?.sku,
            ...compareParamsFromTile(selectedTileData),
            is_easy_ship_order: isEasyShipOrder,
        }));
    }, [
        selectedMarketplace?.id,
        selectedTileData?.start_date,
        selectedTileData?.end_date,
        selectedTileData?.sku,
        selectedTileData?.is_compare,
        selectedTileData?.compare_with_start_date,
        selectedTileData?.compare_with_end_date,
        isEasyShipOrder,
    ]);

    useEffect(() => {
        const currentMarketplaceId = selectedMarketplace?.id;
        const currentStartDate = selectedTileData?.start_date;
        const currentEndDate = selectedTileData?.end_date;
        const currentSku = selectedTileData?.sku;
        const currentCompareParams = compareParamsFromTile(selectedTileData);

        if (
            filter.marketplace_id !== currentMarketplaceId ||
            filter.start_date !== currentStartDate ||
            filter.end_date !== currentEndDate ||
            filter.sku !== currentSku ||
            filter.is_compare !== currentCompareParams.is_compare ||
            filter.compare_with_start_date !== currentCompareParams.compare_with_start_date ||
            filter.compare_with_end_date !== currentCompareParams.compare_with_end_date
        ) {
            setFilter(prev => ({
                ...prev,
                page: 1,
                marketplace_id: currentMarketplaceId,
                start_date: currentStartDate,
                end_date: currentEndDate,
                sku: currentSku,
                ...currentCompareParams,
            }));
        }
    }, [
        selectedMarketplace?.id,
        selectedTileData?.start_date,
        selectedTileData?.end_date,
        selectedTileData?.sku,
        selectedTileData?.is_compare,
        selectedTileData?.compare_with_start_date,
        selectedTileData?.compare_with_end_date,
        filter.marketplace_id,
        filter.start_date,
        filter.end_date,
        filter.sku,
        filter.is_compare,
        filter.compare_with_start_date,
        filter.compare_with_end_date,
    ]);

    useEffect(() => {
        if (filter.marketplace_id && filter.start_date && filter.end_date) {
            fetchOrderList(filter, activeFilters, sortState);
        }
    }, [filter, activeFilters, sortState, fetchOrderList]);

    useEffect(() => {
        if (typeof onFiltersReady !== 'function') return;
        const mergedDropdown = { ...filterDropdownOptions };
        const sellRaw = mergedDropdown.sellable_status;
        const sellEmpty = sellRaw == null
            || (typeof sellRaw === 'object' && !Array.isArray(sellRaw) && Object.keys(sellRaw).length === 0);
        if (sellEmpty) {
            mergedDropdown.sellable_status = { 1: 'Yes', 0: 'No' };
        }
        onFiltersReady({
            filterableColumns: ORDER_ITEMS_FILTERABLE_COLUMNS,
            activeFilters,
            addFilter,
            updateFilter,
            removeFilter,
            clearFilters,
            filterDropdownOptions: mergedDropdown,
        });
    }, [activeFilters, addFilter, updateFilter, removeFilter, clearFilters, onFiltersReady, filterDropdownOptions]);

    const handleOpenModal = (record) => {
        setSelectedRecord(record);
        setIsModalOpen(true);
    };

    // ─── NEW: Bulk-action logic ───────────────────────────────────────────────

    const partitionRows = (rows) => ({
        validRows:   rows.filter(isRowEligible),
        invalidRows: rows.filter((r) => !isRowEligible(r)),
    });

    const callSellableStatusApi = async (rows, isSellableStatus) => {
        const { validRows, invalidRows } = partitionRows(rows);

        if (!validRows.length) {
            message.error("No eligible orders to update (all selected orders have 0 refund units).");
            return;
        }
        if (invalidRows.length > 0) {
            message.warning("Some selected orders cannot be updated due to 0 refund units.");
        }

        const actionKey = isSellableStatus === SELLABLE ? 'sellable' : 'unsellable';
        setActionLoading(actionKey);

        try {
            const amazon_order_ids = validRows.map((r) => r.order_id);
            const response = await MakeApiCall(
                "orders/sellable-status",
                "post",
                { amazon_order_ids, is_sellable_status: isSellableStatus },
                true,
                {},
                userData?.token
            );

            if (response?.status) {
                // Optimistic UI: immediately reflect the new status in the tag
                setStatusMap((prev) => {
                    const updated = { ...prev };
                    amazon_order_ids.forEach((id) => { updated[id] = isSellableStatus; });
                    return updated;
                });
                message.success(
                    `${amazon_order_ids.length} order${amazon_order_ids.length > 1 ? 's' : ''} marked as ${isSellableStatus === SELLABLE ? 'Sellable' : 'Unsellable'} successfully.`
                );
                setSelectedRowKeys([]);
                setSelectedRows([]);
                lastSummaryKeyRef.current = null;
                listFetchGenRef.current += 1;
                setFilter(prev => ({ ...prev }));
            } else {
                message.error(response?.message || "Failed to update sellable status.");
            }
        } catch (error) {
            console.error("Sellable status API error:", error);
            message.error("Something went wrong while updating sellable status.");
        } finally {
            setActionLoading(null);
        }
    };

    const handleMarkSellable   = (rows) => callSellableStatusApi(rows, SELLABLE);
    const handleMarkUnsellable = (rows) => callSellableStatusApi(rows, UNSELLABLE);

    // ─── NEW: rowSelection config ─────────────────────────────────────────────
    const rowSelection = {
        selectedRowKeys,
        onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
        },
        // Disable checkbox when refund_unit === 0
        getCheckboxProps: (record) => ({
            disabled: !isRowEligible(record),
            name: record.order_id,
        }),
        // Wrap disabled checkboxes in a Tooltip (antd doesn't natively do this)
        renderCell: (checked, record, index, originNode) => {
            if (isRowEligible(record)) return originNode;
            return (
                <Tooltip title="Cannot mark this order because refund unit is 0" placement="right">
                    <span>{originNode}</span>
                </Tooltip>
            );
        },
    };

    const footerCellStyle = {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '12px',
        padding: '8px 4px'
    };

    const renderCompareLine = useCallback((record, valuePath, formatter = (value) => value) => {
        const compareWith = record?.compare_with;
        const compareRow = compareWith?.row;
        if (!compareWith || !compareRow) return null;

        const currentValue = getNestedValue(record, valuePath);
        const previousValue = getNestedValue(compareRow, valuePath);
        const hasPreviousValue = previousValue !== undefined && previousValue !== null && previousValue !== '';
        const pctText = formatComparePct(calculateChangePercent(currentValue, previousValue));
        if (!hasPreviousValue && !pctText) return null;

        const pctValue = calculateChangePercent(currentValue, previousValue);
        const pctColor = Number.isFinite(pctValue)
            ? pctValue >= 0 ? '#1a7f37' : '#cf1322'
            : '#7e8299';

        return (
            <div
                className="fs-9 fw-semibold mt-1"
                style={{ color: '#7e8299', lineHeight: 1.2, whiteSpace: 'nowrap' }}
                title={compareWith?.start_date && compareWith?.end_date ? `${compareWith.start_date} to ${compareWith.end_date}` : undefined}
            >
                <span>vs {hasPreviousValue ? formatter(previousValue) : 'prior period'}</span>
                {pctText ? <span className="ms-1" style={{ color: pctColor }}>{pctText}</span> : null}
            </div>
        );
    }, []);

    const renderMetricCell = useCallback((value, record, compareKey, {
        valuePath = compareKey,
        formatter = (v) => v,
        className = '',
        align = 'start',
    } = {}) => {
        const valueClassName = typeof className === 'function' ? className(value) : className;
        return (
            <div className={`d-flex flex-column align-items-${align}`}>
                <span className={valueClassName}>{formatter(value)}</span>
                {renderCompareLine(record, valuePath, formatter)}
            </div>
        );
    }, [renderCompareLine]);

    // --- Desktop Columns (original render functions preserved exactly) ---
    const allColumns = useMemo(() => [
        {
            title: "Product Details",
            key: "userDetails",
            width: 450,
            fixed: 'left',
            render: (_, record) => (
                <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
                    <div className="symbol symbol-65px overflow-hidden me-3">
                        <img src={record?.image_url || fakeProImage} alt="" />
                    </div>
                    <div className="d-flex flex-column">
                        <Tooltip title={record?.product_name || ''}>
                            <span className="text-gray-800 fs-6 fw-bolder text-truncate d-inline-block" style={{ maxWidth: "300px" }}>
                                {record?.product_name || ''}
                            </span>
                        </Tooltip>
                        <div className="fs-7 mt-1">
                            <b>SKU:</b> {record?.sku} / <b>ASIN:</b> {record?.asin}
                        </div>
                        <div className="fs-7 mt-1">
                            Price: <b>{selectedCurrencySign}{AddCommas(record?.price)}</b>
                       
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: "Order Number",
            dataIndex: "order_id",
            key: "order_id",
            width: 250,
            render: (text, record) => (
                <div className="d-flex flex-column">
                    <Tooltip
                        title={
                            <>
                                <div><b>Order ID:</b> {record.order_id}</div>
                                <div><b>Date:</b> {new Date(record.purchase_date).toLocaleString()}</div>
                                <div><b>Status:</b> {record.order_status}</div>
                                <div><b>Channel:</b> {record?.sale_channel}</div>
                                <div><b>COGS:</b> {selectedCurrencySign}{AddCommas(record.product_cogs?.toFixed(2) || 0)}</div>
                           
                            </>
                        }
                    >
                        <span className="fw-bold text-dark cursor-pointer text-truncate d-inline-block" style={{ maxWidth: "180px" }}>
                            {record.order_id}
                        </span>
                    </Tooltip>
                    <div className="fs-7 text-gray-800">
                        {new Date(record.purchase_date).toLocaleString()} • {record.order_status}
                    </div>
                    <div className="fs-7 text-gray-800">
                        {record?.sale_channel} / COGS: <b>{selectedCurrencySign}{AddCommas(record.product_cogs?.toFixed(2) || 0)}</b>
                   
                    </div>
                </div>
            ),
        },
        { 
            title: "Units Sold", 
            dataIndex: "unit_sold", 
            key: "unit_sold", 
            width: 120, 
            render: (text, record) => renderMetricCell(text, record, "unit_sold", { formatter: AddCommas }),
            sorter: true,
            sortOrder: sortState.columnKey === 'unit_sold' ? sortState.order : null,
        },
        { 
            title: "Refund Units", 
            dataIndex: "refund_unit", 
            key: "refund_unit", 
            width: 130, 
            render: (text, record) => renderMetricCell(text, record, "refund_unit", { formatter: AddCommas }),
            sorter: true,
            sortOrder: sortState.columnKey === 'refund_unit' ? sortState.order : null,
        },
        { 
            title: "Sales", 
            dataIndex: "sales", 
            key: "sales", 
            width: 120, 
            render: (text, record) => renderMetricCell(text, record, "sales", {
                formatter: (value) => getAmount(value, selectedCurrencySign),
            }),
            sorter: true,
            sortOrder: sortState.columnKey === 'sales' ? sortState.order : null,
        },
   
        {
            title: "Amazon Fees",
            key: "amazon_fees",
            width: 140,
            render: (_, record) => {
                const totalFee = record.amazon_fees || 0;
                const tooltipContent = (
                    <AmazonFeesBreakdownTooltip
                        amazonFees={record.more_info?.amazon_fees}
                        currencySign={selectedCurrencySign}
                    />
                );
                return (
                    <div className="d-flex flex-column align-items-start">
                        <Tooltip
                            title={tooltipContent}
                            placement="top"
                            color="#1e1e2d"
                            styles={{ body: amazonFeesTooltipBodyStyle }}
                        >
                            <span className="fw-bolder text-danger cursor-pointer">
                                {getAmount(totalFee, selectedCurrencySign)}
                            </span>
                        </Tooltip>
                        {renderCompareLine(record, 'amazon_fees', (value) => getAmount(value, selectedCurrencySign))}
                    </div>
                );
            },
            sorter: true,
            sortOrder: sortState.columnKey === 'amazon_fees' ? sortState.order : null,
        },
        { title: "Gross Profit", dataIndex: "gross_profit", key: "gross_profit", width: 130, render: (text, record) => renderMetricCell(text, record, "gross_profit", { formatter: (value) => getAmount(value, selectedCurrencySign) }), sorter: true, sortOrder: sortState.columnKey === 'gross_profit' ? sortState.order : null },
        {
            title: "Promo",
            key: "promo",
            width: 120,
            render: (_, record) => renderMetricCell(record.more_info?.promo, record, "promo", {
                valuePath: ['more_info', 'promo'],
                formatter: (value) => getAmount(value, selectedCurrencySign),
            }),
            sorter: true,
            sortOrder: sortState.columnKey === 'promo' ? sortState.order : null,
        },
        {
            title: "Gift Wrap",
            key: "gift_wrap",
            width: 120,
            render: (_, record) => renderMetricCell(record.more_info?.giftwrap, record, "gift_wrap", {
                valuePath: ['more_info', 'giftwrap'],
                formatter: (value) => getAmount(value, selectedCurrencySign),
            }),
            sorter: true,
            sortOrder: sortState.columnKey === 'gift_wrap' ? sortState.order : null,
        },
        {
            title: "Shipping Cost",
            key: "shipping_cost",
            width: 140,
            render: (_, record) => renderMetricCell(record.more_info?.shipping_cost, record, "shipping_cost", {
                valuePath: ['more_info', 'shipping_cost'],
                formatter: (value) => getAmount(value, selectedCurrencySign),
            }),
            sorter: true,
            sortOrder: sortState.columnKey === 'shipping_cost' ? sortState.order : null,
        },
        {
            title: "Cost of Goods",
            key: "cost_of_goods",
            width: 140,
            render: (_, record) => renderMetricCell(record.more_info?.cost_of_goods, record, "cost_of_goods", {
                valuePath: ['more_info', 'cost_of_goods'],
                formatter: (value) => getAmount(value, selectedCurrencySign),
            }),
            sorter: true,
            sortOrder: sortState.columnKey === 'cost_of_goods' ? sortState.order : null,
        },
        {
            title: "VAT",
            key: "vat",
            width: 110,
            render: (_, record) => renderMetricCell(record.more_info?.vat, record, "vat", {
                valuePath: ['more_info', 'vat'],
                formatter: (value) => getAmount(value, selectedCurrencySign),
            }),
            sorter: true,
            sortOrder: sortState.columnKey === 'vat' ? sortState.order : null,
        },
        {
            title: "Net Profit",
            key: "net_profit",
            width: 130,
            render: (_, record) => renderMetricCell(record.more_info?.net_profit, record, "net_profit", {
                valuePath: ['more_info', 'net_profit'],
                formatter: (value) => getAmount(value, selectedCurrencySign),
            }),
            sorter: true,
            sortOrder: sortState.columnKey === 'net_profit' ? sortState.order : null,
        },
        {
            title: "Estimated Payout",
            key: "estimated_payout",
            width: 150,
            render: (_, record) => renderMetricCell(record.more_info?.estimated_payout, record, "estimated_payout", {
                valuePath: ['more_info', 'estimated_payout'],
                formatter: (value) => getAmount(value, selectedCurrencySign),
            }),
            sorter: true,
            sortOrder: sortState.columnKey === 'estimated_payout' ? sortState.order : null,
        },
        // ─── Sellable Status column ───────────────────────────────────────────
        {
            title: "Sellable Status",
            key: "sellable_status",
            width: 150,
            render: (_, record) => <SellableTag status={resolveStatus(record, statusMap)} />,
        },
        {
            title: "Info",
            key: "info",
            width: 120,
            render: (_, record) => (
                <span className="cursor-pointer fw-bold  text-dark text-hover-danger" onClick={() => handleOpenModal(record)}>
                    See Details
                </span>
            ),
        },
    ], [selectedCurrencySign, statusMap, sortState, renderMetricCell, renderCompareLine]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Column visibility ────────────────────────────────────────────────────
    const userId = userData?.user?.user_data?.id ?? userData?.user?.id;
    const allColumnKeys = useMemo(() => allColumns.map(col => col.key), [allColumns]);

    const { visibleColumns, handleColumnToggle } = useVisibleColumns({
        pageId: COLUMN_PREF_PAGES.ORDER_ITEMS,
        userId,
        allColumnKeys,
        fixedColumnKeys: ['userDetails', 'info'],
        hiddenByDefaultKeys: [
            'promo', 'gift_wrap', 'shipping_cost', 'cost_of_goods', 'vat',
            'net_profit', 'estimated_payout', 'sellable_status',
        ],
    });

    const columns = useMemo(
        () => allColumns.filter(col => visibleColumns.includes(col.key)),
        [allColumns, visibleColumns]
    );

    const toggleableColumns = useMemo(
        () => allColumns.filter(col => col.key !== 'userDetails' && col.key !== 'info'),
        [allColumns]
    );

    // Surface column controls to parent
    useEffect(() => {
        if (typeof onColumnsReady !== 'function') return;
        onColumnsReady({ toggleableColumns, visibleColumns, handleColumnToggle });
    }, [toggleableColumns, visibleColumns, handleColumnToggle, onColumnsReady]);

    // --- Mobile Card Rendering (original structure preserved exactly) ---
    const renderMobileCards = () => (
        <div className="d-flex flex-column gap-3 cus-order-list">
            {list.map((item, index) => (
                <div
                    key={item.order_id || index}
                    className="card border-0 shadow-sm p-4 rounded-4 cursor-pointer"
                    style={{ background: 'var(--app-surface)' }}
                    // onClick={() => handleOpenModal(item)}
                >
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <div className="fs-9 text-uppercase text-muted fw-bold">Order Number</div>
                            <div className="fw-bolder text-primary fs-7">{item.order_id}</div>
                        </div>
                        <div className="text-end d-flex align-items-center gap-4">
                            <div className="fs-9 text-muted">{new Date(item.purchase_date).toLocaleDateString()}</div>
                            <span className="badge badge-light-success fs-9 px-2 py-1">{item.order_status}</span>
                            {/* NEW: sellable status tag */}
                            <div className="mt-1"><SellableTag status={resolveStatus(item, statusMap)} /></div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center mb-4 bg-light-gray p-2 rounded-3" style={{ background: '#e8edf169' }}>
                        <img src={item.image_url || fakeProImage} alt="pro" className="rounded-2 me-3" style={{ width: '45px', height: '45px', objectFit: 'contain' }} />
                        <div className="overflow-hidden">
                            <div className="fw-bold text-dark text-truncate fs-7" style={{ maxWidth: '200px' }}>{item.product_name || 'N/A'}</div>
                            <div className="text-muted fs-9">SKU: {item.sku} | ASIN: {item.asin}</div>
                        </div>
                    </div>

                    <div className="row g-3 border-top pt-3">
                        <div className="col-4 border-end">
                            <div className="fs-9 text-muted fw-bold mb-1">Units</div>
                            {renderMetricCell(item.unit_sold, item, "unit_sold", {
                                formatter: AddCommas,
                                className: 'fw-bolder text-dark',
                            })}
                        </div>
                        <div className="col-4 border-end text-center">
                            <div className="fs-9 text-muted fw-bold mb-1">Refunds</div>
                            {renderMetricCell(item.refund_unit, item, "refund_unit", {
                                formatter: AddCommas,
                                className: 'fw-bolder text-danger',
                                align: 'center',
                            })}
                        </div>
                        <div className="col-4 text-end">
                            <div className="fs-9 text-muted fw-bold mb-1">Sales</div>
                            {renderMetricCell(item.sales, item, "sales", {
                                formatter: (value) => getAmount(value, selectedCurrencySign),
                                className: 'fw-bolder text-dark',
                                align: 'end',
                            })}
                        </div>

                        <div className="col-6 mt-3">
                            <div className="fs-9 text-muted fw-bold mb-1">Amazon Fees</div>
                            {renderMetricCell(item.amazon_fees, item, "amazon_fees", {
                                formatter: (value) => getAmount(value, selectedCurrencySign),
                                className: 'fw-bolder text-danger',
                            })}
                        </div>
                        <div className="col-6 mt-3 text-end">
                            <div className="fs-9 text-muted fw-bold mb-1">Gross Profit</div>
                            {renderMetricCell(item.gross_profit, item, "gross_profit", {
                                formatter: (value) => getAmount(value, selectedCurrencySign),
                                className: 'fw-bolder text-success fs-6',
                                align: 'end',
                            })}
                        </div>
                    </div>

                    {/* NEW: per-card action buttons, only shown when eligible (refund_unit > 0) */}
                    {isRowEligible(item) && (
                        <div className="d-flex gap-2 mt-3 border-top pt-3" onClick={(e) => e.stopPropagation()}>
                            <Button
                                size="small"
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                loading={actionLoading === 'sellable'}
                                disabled={!!actionLoading}
                                onClick={() => handleMarkSellable([item])}
                                style={{ background: '#52c41a', borderColor: '#52c41a', flex: 1 }}
                            >
                                Sellable
                            </Button>
                            <Button
                                size="small"
                                type="primary"
                                danger
                                icon={<CloseCircleOutlined />}
                                loading={actionLoading === 'unsellable'}
                                disabled={!!actionLoading}
                                onClick={() => handleMarkUnsellable([item])}
                                style={{ flex: 1 }}
                            >
                                Unsellable
                            </Button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );

    const renderMobileCardLoader = () => (
        <div className="d-flex flex-column gap-3 cus-order-list">
            {Array.from({ length: 4 }).map((_, index) => (
                <div
                    key={index}
                    className="card border-0 shadow-sm p-4 rounded-4 placeholder-glow"
                    style={{ background: 'var(--app-surface)' }}
                >
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1 pe-3">
                            <span className="placeholder col-5 rounded d-block mb-2" style={{ height: 9 }} />
                            <span className="placeholder col-8 rounded d-block" style={{ height: 14 }} />
                        </div>
                        <div className="d-flex align-items-center gap-3 justify-content-end" style={{ minWidth: 130 }}>
                            <span className="placeholder rounded d-block" style={{ width: 48, height: 10 }} />
                            <span className="placeholder rounded-pill d-block" style={{ width: 58, height: 18 }} />
                        </div>
                    </div>

                    <div className="d-flex align-items-center mb-4 p-2 rounded-3" style={{ background: '#e8edf169' }}>
                        <span
                            className="placeholder rounded-2 me-3"
                            style={{ width: 45, height: 45, display: 'inline-block', flexShrink: 0 }}
                        />
                        <div className="flex-grow-1">
                            <span className="placeholder col-10 rounded d-block mb-2" style={{ height: 13 }} />
                            <span className="placeholder col-7 rounded d-block" style={{ height: 10 }} />
                        </div>
                    </div>

                    <div className="row g-3 border-top pt-3">
                        {Array.from({ length: 5 }).map((__, metricIndex) => (
                            <div className={metricIndex < 3 ? "col-4" : "col-6 mt-3"} key={metricIndex}>
                                <span className="placeholder col-8 rounded d-block mb-2" style={{ height: 9 }} />
                                <span className="placeholder col-10 rounded d-block" style={{ height: 14 }} />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );


    return (
        <div>
            {/* NEW: Bulk Actions Bar — desktop only, conditionally rendered when rows are selected */}
            {!isMobile && (
                <BulkActionsBar
                    selectedRows={selectedRows}
                    onMarkSellable={handleMarkSellable}
                    onMarkUnsellable={handleMarkUnsellable}
                    actionLoading={actionLoading}
                />
            )}

            {isMobile ? (
                <div className="p-1">
                    {loading
                        ? renderMobileCardLoader()
                        : list.length > 0
                            ? renderMobileCards()
                            : <Empty description="No Orders Found" />}
                </div>
            ) : (
                <div ref={tableContainerRef} className="shadow-sm rounded">
                    <Table
                        rowSelection={rowSelection}
                        columns={columns}
                        loading={loading}
                        dataSource={Array.isArray(list) ? list : []}
                        rowKey={(record) => record.order_id}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        sticky={{ offsetHeader: bannerHeight }}
                        onChange={handleTableChange}
                        showSorterTooltip={false}
                        summary={() => {
                            if (!summaryData) return null;

                            const summaryDataMap = {
                                unit_sold:    summaryData.unit_sold,
                                refund_unit:  summaryData.refund_unit,
                                sales:        getAmount(summaryData.sales, selectedCurrencySign),
                                amazon_fees:  <span style={{ color: '#f1416c' }}>{getAmount(summaryData.amazon_fees, selectedCurrencySign)}</span>,
                                gross_profit: getAmount(summaryData.gross_profit, selectedCurrencySign),
                            };

                            return (
                                <Table.Summary fixed>
                                    <Table.Summary.Row className="bg-light fw-bolder">
                                        {/* checkbox column placeholder */}
                                        <Table.Summary.Cell index={0} />
                                        {columns.map((col, index) => (
                                            <Table.Summary.Cell key={col.key} index={index + 1}>
                                                {col.key === 'userDetails'
                                                    ? <div className="ps-2 fw-bold">TOTALS</div>
                                                    : <div style={footerCellStyle}>{summaryDataMap[col.key] ?? ''}</div>
                                                }
                                            </Table.Summary.Cell>
                                        ))}
                                    </Table.Summary.Row>
                                </Table.Summary>
                            );
                        }}
                    />
                </div>
            )}

            <ConfigProvider theme={theme}>
                <div className={`d-flex ${isMobile ? 'justify-content-center' : 'justify-content-end'} mt-3`}>
                    <Pagination
                        current={filter.page}
                        pageSize={filter['per-page']}
                        total={totalPage}
                        showSizeChanger
                        simple={isMobile}
                        onChange={(currentPage, newPageSize) => {
                            setFilter(prev => ({
                                ...prev,
                                page: currentPage,
                                "per-page": newPageSize,
                            }));
                        }}
                        showTotal={(total, range) => isMobile ? `${total} Orders` : `${range[0]}-${range[1]} of ${total} items`}
                    />
                </div>
            </ConfigProvider>

            <OrderDetailsModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                data={selectedRecord}
                currencySign={selectedCurrencySign}
            />
        </div>
    );
};

export default OrderItems;